import React from 'react';
import { CurrentHouseProvider } from '@/context/CurrentHouseContext';
import {
	isFilterablePlanCategory,
	type FilterablePlanCategory,
} from '@/components/maintenance/model/planCategoryLabels';
import Schedule from '../../../../components/maintenance/plan/Schedule';

/**
 * Schedule page scoped to a specific property via its public ID in the URL.
 *
 * What a card offers comes from its own `allowed_actions`, already state-gated by the endpoint, so
 * there is no switch here.
 *
 * Upcoming reads the v2 plan through the same components Storybook documents; History still reads
 * the v1 completion endpoints, which have no v2 equivalent yet.
 */
const SchedulePage: React.FC = () => {
	return (
		<CurrentHouseProvider>
			<Schedule />
		</CurrentHouseProvider>
	);
};

export const Route = createFileRoute({
	component: SchedulePage,
	validateSearch: (search) => {
		return {
			// A plan node is keyed by an opaque string; the numeric pair still addresses v1 History.
			planNodeId: search.planNodeId ? String(search.planNodeId) : undefined,
			// Which part of the screen is open. In the URL for the same reason the filters are: a link
			// to a record should land on the record, and the back button should step out of it.
			// `as-needed` named this tab while it held only undated work; `not-scheduled` and
			// `not-planned` named it briefly after that. It now holds everything the household
			// deferred, so an old link normalises onto it rather than landing somewhere else.
			tab:
				search.tab === 'history'
					? ('history' as const)
					: search.tab === 'unscheduled' ||
						  search.tab === 'not-planned' ||
						  search.tab === 'not-scheduled' ||
						  search.tab === 'as-needed'
						? ('unscheduled' as const)
						: undefined,
			// Which shape of deferred work the Unscheduled tab shows. Absent means all of it, so the
			// default keeps the URL clean and a shared link opens where the sender was.
			show:
				search.show === 'dated' || search.show === 'as-needed'
					? (search.show as 'dated' | 'as-needed')
					: undefined,
			// Which kind of obligation the Scheduled tab shows. A lens over the cards the tab already
			// holds, not the `category` facet below — two controls writing one parameter would fight.
			kind: isFilterablePlanCategory(String(search.kind ?? ''))
				? (search.kind as FilterablePlanCategory)
				: undefined,
			// A link asking for the review queue, which the cards-to-review notification sends. In the
			// URL so the request survives the navigation rather than being inferred on arrival.
			review: search.review === 'open' ? ('open' as const) : undefined,
			// One dated instance in History, as `<nodeId>@<date>` — an occurrence has no durable id
			// of its own, so the card and the date are what address it. See `occurrenceKey`.
			occurrence: search.occurrence ? String(search.occurrence) : undefined,
			// One comma-separated param per facet. In the URL so a filtered schedule is shareable and
			// the back button steps through filters, the same as the open drawer.
			category: search.category ? String(search.category) : undefined,
			displayCategory: search.displayCategory ? String(search.displayCategory) : undefined,
			status: search.status ? String(search.status) : undefined,
			origin: search.origin ? String(search.origin) : undefined,
			systemDomain: search.systemDomain ? String(search.systemDomain) : undefined,
			maintenanceTaskId: search.maintenanceTaskId ? Number(search.maintenanceTaskId) : undefined,
			maintenanceCompletionId: search.maintenanceCompletionId
				? Number(search.maintenanceCompletionId)
				: undefined,
		};
	},
});
